<template>
  <v-container class="asset-aoi-editor-container">
    <v-row row wrap align-content-start class="content-container">
      <v-col class="col-4 py-0 my-0">
        <v-container class="py-0 my-0">
          <v-row row wrap>
            <v-col class="col-12 pt-0">
              <div class="aoi-results-container">
                <Loader v-if="!enabledSelection" text="" size="small"></Loader>

                <div class="scrollable" v-if="buttonLocationEnabled">
                  <p class="location-text">
                    Enter a location, address or coordinate (latitude,
                    longitude) in the location field below OR right click your
                    mouse on the map to manually select your location.
                  </p>
                  <p class="location-text">
                    Once a location has been set the map will show an icon
                    <img :src="iconSrc" height="18" class="location-icon" />
                    at the location and the address, country, latitude and
                    longitude fields under the map will be filled.
                  </p>
                  <p class="location-text">
                    Note: Asset latitude and longitude only accepts five decimal
                    points
                  </p>
                  <p>
                    <v-text-field
                      id="locationField"
                      type="text"
                      placeholder=""
                      label="Location"
                      @keydown="handleKeyDownLocationField"
                      class="pt-0"
                    ></v-text-field>
                  </p>
                  <p
                    v-if="location && location.place && location.place.geometry"
                  >
                    Formatted Address:<br />{{ location.place.formatted_address
                    }}<br /><br />
                    Coordinates:<br />{{
                      location.place.geometry.location.lat().toFixed(5)
                    }}, {{ location.place.geometry.location.lng().toFixed(5) }}
                  </p>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-col>

      <v-col class="text-center col-8 pt-0">
        <Map
          v-if="hasWebGL"
          :id="mapId"
          height="27vh"
          :show-base-layer-control="false"
          :show-places="false"
          :options="mapOptions"
          class="hyp-asset-edit-map"
          @map-on-load="handleMapOnLoad"
          @map-on-context-menu="handleMapOnContext"
        ></Map>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import { MODULE_PREFIX_MAP, MODULE_PREFIX_SEARCH } from "@/store";
import { removeMapData } from "@/common/maps/features";
import { addSimplePointOnMap, reverseGeocode } from "@/common/maps/utilities";
import { MAP_ICON_NAMES } from "@/common/constants/maps";
import { ASSET_AOI_EDITOR_MAP } from "@/common/constants/assetManagement";

const EMITTED_ACTIONS = {
  CHANGE: "change",
};

export default {
  name: "AssetsEditor",
  components: {
    Map: () => import("@/components/map/Map"),
    Loader: () => import("@/components/common/Loader"),
  },
  props: {
    asset: {
      type: Object,
    },
  },
  data() {
    return {
      hasWebGL: this.isAvailableWebGL(),
      mapId: ASSET_AOI_EDITOR_MAP,
      mapLoaded: false,
      mapOptions: {
        zoom: 0,
        bounds: [-175, -85, 178, 85],
        attributionCompact: true,
        openSearchOnMapLoad: true,
      },
      enabledSelection: "location",
      isSelectionMandatory: false,
      simplePointLayerId: "search-place",
      autocomplete: null,
      inputLocation: null,
      listenerLocation: null,
    };
  },
  async mounted() {
    this.inputLocation = document.getElementById("locationField");

    this.autocomplete = new google.maps.places.Autocomplete(
      this.inputLocation,
      { types: ["geocode"] }
    );

    this.listenerLocation = google.maps.event.addListener(
      this.autocomplete,
      "place_changed",
      async () => {
        if (this.autocomplete) {
          const place = this.autocomplete.getPlace();
          if (place.geometry) {
            this.updateMap(
              place.geometry.location.lat(),
              place.geometry.location.lng()
            );
            this.$emit(EMITTED_ACTIONS.CHANGE, place);
          } else if (place.name) {
            const parts = place.name.split(",");
            if (
              parts &&
              Array.isArray(parts) &&
              parts.length === 2 &&
              parts[0] &&
              parts[1]
            ) {
              const latitude = parseFloat(parts[0]);
              const longitude = parseFloat(parts[1]);
              if (
                latitude >= -90 &&
                latitude <= 90 &&
                longitude >= -180 &&
                longitude <= 180
              ) {
                await this.reverseGeocodeLatLng(latitude, longitude);
              }
            }
          }
        }
      }
    );
  },
  computed: {
    ...mapGetters({
      maps: MODULE_PREFIX_MAP + "maps",
      location: MODULE_PREFIX_SEARCH + "location",
    }),
    map() {
      return this.maps[this.mapId];
    },
    buttonLocationEnabled() {
      return this.enabledSelection === "location";
    },
    iconSrc() {
      return require(`@/assets/images/icons/places_green.png`);
    },
  },
  methods: {
    async handleKeyDownLocationField(e) {
      const code = e.keyCode || e.which;
      if (code === 13) {
        e.preventDefault();
      }
    },
    async handleMapOnLoad() {
      this.mapLoaded = true;
      this.map.resize();
      this.handleAssetChange();
    },
    async handleMapOnContext(e) {
      if (e?.lngLat?.lat && e?.lngLat?.lng) {
        await this.reverseGeocodeLatLng(e.lngLat.lat, e.lngLat.lng, false);
      }
    },
    handleAssetChange() {
      if (this.asset) {
        if (this.asset.longitude && this.asset.latitude) {
          this.updateMap(this.asset.latitude, this.asset.longitude);
        } else {
          this.resetMap();
        }
      }
    },
    removePointLayer() {
      removeMapData(this.map, this.simplePointLayerId);
    },
    resetMap() {
      if (this.map) {
        this.removePointLayer();
        this.map.fitBounds(
          [
            [-160, -70],
            [160, 70],
          ],
          { padding: 20 }
        );
      }
    },
    updateMap(lat, lng, flyToAfterUpdate = true) {
      if (lat && lng) {
        const point = [lng, lat];
        addSimplePointOnMap({
          point: point,
          layerId: this.simplePointLayerId,
          map: this.map,
          iconName: MAP_ICON_NAMES.locationsEdit,
        });

        if (flyToAfterUpdate) {
          this.map.flyTo({
            zoom: 7,
            center: point,
          });
        }
      }
    },
    async reverseGeocodeLatLng(latitude, longitude, flyToAfterUpdate = true) {
      const providedLocation = {
        lat: latitude,
        lng: longitude,
      };
      const googlePlace = await reverseGeocode(providedLocation);
      googlePlace.reverseGeocoded = true;
      googlePlace.provided_location = providedLocation;
      this.updateMap(latitude, longitude, flyToAfterUpdate);
      this.$emit(EMITTED_ACTIONS.CHANGE, googlePlace);
    },
  },
  watch: {
    asset: {
      handler() {
        this.handleAssetChange();
      },
      deep: true,
    },
  },
  beforeDestroy() {},
};
</script>

<style scoped>
.asset-aoi-editor-container {
  padding: 0;
}
.aoi-results-container {
  min-height: 200px;
  padding: 10px;
}
.aoi-chip-span {
  max-width: 210px;
  overflow-x: hidden;
  text-overflow: ellipsis;
}
.aoi-chip >>> .v-chip__content {
  height: 24px;
  border-radius: 16px;
  padding: 0 6px;
}
.underline {
  text-decoration: underline;
}
.hyp-selection-btn >>> .v-btn__content {
  padding: 0 10px;
}
.hyp-text-helper {
  font-size: 12px;
}
.scrollable {
  overflow-y: auto;
  max-height: 50vh;
}
.location-icon {
  position: relative;
  top: 3px;
}
.location-text {
  font-size: 13px;
}
</style>
